import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_ADMIN_TAGS,
  UPDATE_ADMIN_TAGS,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import { isReadonly } from "../../app/features/auth/authTypes";
import ActionButton from "./ActionButton";
import ActionIconButton from "./ActionIconButton";
import useConfirmNewValue from "./useConfirmNewValue";

// Tags are kept the way the backend stores them: trimmed and upper case.
const normalize = (tag: string) => tag.trim().toUpperCase();

const splitTags = (tags: string | null | undefined) =>
  (tags ?? "").split(",").map(normalize).filter(Boolean);

// The admin tags of a stone, on their own line under its tags, with the round button that edits
// them. Admins only: notes for whoever runs the stock — a picture to retake, a stone to look at
// again — which mean nothing to the pipeline.
const AdminTags = ({
  diamondID,
  sku,
  tags,
}: {
  diamondID: string;
  sku?: string;
  tags?: string | null;
}) => {
  const role = useAppSelector((state) => state.auth.role);
  const [current, setCurrent] = useState<string[]>(splitTags(tags));
  const [editing, setEditing] = useState(false);

  // The line shows what was last saved; a refetch of the stone brings the stored value back here.
  useEffect(() => setCurrent(splitTags(tags)), [tags]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        mt: 1,
        flexWrap: "wrap",
      }}
    >
      {current.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No admin tags
        </Typography>
      ) : (
        current.map((tag) => (
          <Chip
            key={tag}
            size="small"
            color="warning"
            variant="outlined"
            icon={<AdminPanelSettingsIcon />}
            label={tag}
          />
        ))
      )}
      {/* A read-only user sees the tags, and cannot change them. */}
      {!isReadonly(role) && (
        <Tooltip title="Edit admin tags">
          <ActionIconButton
            aria-label="Edit admin tags"
            onClick={() => setEditing(true)}
            sx={{ ml: 0.5, width: 26, height: 26 }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </ActionIconButton>
        </Tooltip>
      )}
      {editing && (
        <AdminTagsDialog
          diamondID={diamondID}
          sku={sku}
          tags={current}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setCurrent(saved);
            setEditing(false);
          }}
        />
      )}
    </Box>
  );
};

// The editor: the stone's admin tags as chips, added from the tags already in use or typed new. A
// tag never used before is confirmed first, so a typo does not quietly become a tag of its own.
const AdminTagsDialog = ({
  diamondID,
  sku,
  tags,
  onClose,
  onSaved,
}: {
  diamondID: string;
  sku?: string;
  tags: string[];
  onClose: () => void;
  onSaved: (tags: string[]) => void;
}) => {
  const token = useAppSelector((state) => state.auth.token);
  const { data } = useQuery(GET_ADMIN_TAGS, tokenOptions(token));
  const [updateAdminTags, { loading: saving }] = useMutation(
    UPDATE_ADMIN_TAGS,
    {
      ...tokenOptions(token),
      // The tags in use, and the stone everywhere it is shown — the pages load it by itself or
      // through one of its pictures.
      refetchQueries: ["AdminTags", "Diamonds", "Images"],
    },
  );
  const known: string[] = data?.getAdminTags ?? [];
  const [draft, setDraft] = useState<string[]>(tags);
  const [typed, setTyped] = useState("");
  const confirmNew = useConfirmNewValue("admin tag");

  const handleChange = async (values: string[]) => {
    const next = Array.from(new Set(values.map(normalize).filter(Boolean)));
    const created = next.find(
      (tag) => !known.includes(tag) && !draft.includes(tag),
    );
    // A new tag joins only once confirmed; the rest of the change applies either way.
    if (created && !(await confirmNew(created))) {
      setDraft(next.filter((tag) => tag !== created));
    } else {
      setDraft(next);
    }
  };

  // Leaving the field keeps what was typed but not entered — never the option the list happens to
  // have highlighted, which MUI's autoSelect would add whenever the open list loses focus.
  const handleBlur = () => {
    if (typed.trim()) {
      handleChange([...draft, typed]);
      setTyped("");
    }
  };

  const unchanged =
    draft.length === tags.length && draft.every((tag) => tags.includes(tag));

  const handleSave = async () => {
    await updateAdminTags({ variables: { diamondID, tags: draft } });
    onSaved(draft);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Admin tags{sku ? ` of ${sku}` : ""}</DialogTitle>
      <DialogContent>
        <Autocomplete
          multiple
          freeSolo
          filterSelectedOptions
          disableCloseOnSelect
          options={known}
          value={draft}
          onChange={(_, values) => handleChange(values as string[])}
          inputValue={typed}
          onInputChange={(_, input) => setTyped(input)}
          onBlur={handleBlur}
          renderTags={(values, getTagProps) =>
            values.map((tag, index) => (
              <Chip
                {...getTagProps({ index })}
                key={tag}
                size="small"
                color="warning"
                variant="outlined"
                icon={<AdminPanelSettingsIcon />}
                label={tag}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              placeholder="Type to search, or enter a new tag"
              autoFocus
            />
          )}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <ActionButton onClick={handleSave} disabled={saving || unchanged}>
          Save
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdminTags;
